// @ts-nocheck
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';
import { Action, Chip, Field, Input, Panel, PanelHead, Toggle } from '../../components/aa';
import { hasScreens } from '../../lib/rolesWithoutScreens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * One Identity P5.1 — the platform role registry (super only).
 *
 * Author a role here and it appears in every client's team picker with no
 * deploy (the picker reads the registry table). Grant its permissions in the
 * matrix below — same role name, same grants table. Staff-only roles
 * (Expert, Consultant) are platform-assigned and can never be made
 * client-assignable.
 */
const RoleRegistryPanel = () => {
    const [rows, setRows] = useState([]);
    const [label, setLabel] = useState('');
    const [rank, setRank] = useState(5);
    const [saving, setSaving] = useState(false);
    const [adding, setAdding] = useState(false);

    const load = async () => {
        try {
            const r = await axios.get(`${API_BASE_URL}/api/roles/registry`);
            setRows((r.data || []).filter((row) => hasScreens(row.value)));
        } catch (e) {
            toast.error('Could not load the role registry');
        }
    };
    useEffect(() => { load(); }, []);

    const createRole = async (e) => {
        e.preventDefault();
        if (!label.trim()) { toast.error('Give the role a name'); return; }
        setSaving(true);
        try {
            const value = label.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
            await axios.post(`${API_BASE_URL}/api/roles/registry`, {
                value, label: label.trim(), rank: Number(rank), assignable: true,
            });
            toast.success(`Role "${label.trim()}" is live — it now shows in every client's team picker`);
            setLabel('');
            setAdding(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not create the role');
        } finally { setSaving(false); }
    };

    const toggleAssignable = async (row) => {
        try {
            await axios.patch(`${API_BASE_URL}/api/roles/registry/${row.value}`, { assignable: !row.assignable });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not update the role');
        }
    };

    let saveLabel = 'Create role';
    if (saving) saveLabel = 'Creating…';

    return (
        <Panel>
            <PanelHead
                title="Platform roles"
                desc="Roles a client can give their team. Lower rank means more authority."
                action={!adding && (
                    <Action size="md" variant="quiet" icon={<Plus size={15} />} onClick={() => setAdding(true)}>New role</Action>
                )}
            />

            {adding && (
                <form onSubmit={createRole} className="mx-5 mt-4 flex flex-wrap items-end gap-3 rounded-[12px] bg-canvas-inset p-4 md:mx-6">
                    <Field label="Role name" className="min-w-[200px] flex-1">
                        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Reviewer" autoFocus />
                    </Field>
                    <Field label="Rank (0 is highest)" className="w-32">
                        <Input type="number" min={0} max={99} value={rank} onChange={(e) => setRank(e.target.value)} />
                    </Field>
                    <div className="flex gap-2">
                        <Action type="button" variant="ghost" onClick={() => setAdding(false)}>Cancel</Action>
                        <Action type="submit" disabled={saving}>{saveLabel}</Action>
                    </div>
                </form>
            )}

            <div className="overflow-x-auto px-5 pb-3 md:px-6">
                <table className="w-full min-w-[520px] text-[13px]">
                    <thead>
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-dim">
                            <th className="py-3 pr-4 font-semibold">Role</th>
                            <th className="w-24 py-3 pr-4 font-semibold">Rank</th>
                            <th className="w-56 py-3 font-semibold">Clients can assign</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.value} className="border-t border-line-soft">
                                <td className="py-3 pr-4">
                                    <div className="font-semibold text-ink">{r.label}</div>
                                    <div className="mt-0.5 font-code text-[11px] text-ink-soft">{r.value}</div>
                                </td>
                                <td className="py-3 pr-4 tabular-nums text-ink-muted">{r.rank}</td>
                                <td className="py-3">
                                    {r.staff_only && <Chip tone="neutral">Staff only</Chip>}
                                    {!r.staff_only && (
                                        <Toggle checked={Boolean(r.assignable)} onChange={() => toggleAssignable(r)} label={`Clients can assign ${r.label}`} />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Panel>
    );
};

export default RoleRegistryPanel;
